/**
 * Phase RMA-5 — RMA / Warranty claims sidebar card on DiagnosticReportPage.
 *
 * Self-contained: fetches its own data on mount/reportId change. Mounted
 * only when the page detects an office user — gating happens in the parent.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PlusIcon } from "@phosphor-icons/react";

import { ErrorBanner, PrimaryButton, SecondaryButton } from "../crm/Form";
import RMAStatusBadge from "./RMAStatusBadge";
import CreateRMAModal from "./CreateRMAModal";
import { formatApiError } from "../../lib/api";
import {
  getReportRMARecords,
  RMA_VENDOR_LABEL,
  shortId,
} from "../../lib/rma";

export default function RMARelatedCard({ reportId, workOrderId }) {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    if (!reportId) return;
    setLoading(true);
    setErr("");
    try {
      const data = await getReportRMARecords(reportId);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    load();
  }, [load]);

  const lockedLabel = workOrderId ? `WO-${shortId(workOrderId)}` : "";

  return (
    <section
      data-testid="rma-related-card"
      className="bg-white border border-slate-200 rounded-md p-4"
    >
      <div className="font-heading text-sm font-semibold text-slate-900">
        RMA / Warranty claims
      </div>
      <div className="text-[11px] text-slate-500 mt-0.5 mb-3">
        Vendor warranty workflow tied to this report
      </div>

      {err && <ErrorBanner message={err} />}

      {loading && !err && (
        <div className="space-y-2" data-testid="rma-card-loading">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-8 rounded-md bg-slate-100 animate-pulse"
            />
          ))}
        </div>
      )}

      {!loading && !err && items.length === 0 && (
        <div data-testid="rma-card-empty" className="space-y-3">
          <div className="text-xs text-slate-600">
            No RMA claims linked to this report.
          </div>
          <PrimaryButton
            type="button"
            onClick={() => setCreateOpen(true)}
            className="w-full"
            data-testid="rma-card-create-empty"
          >
            <PlusIcon size={14} className="mr-1.5" /> Create RMA
          </PrimaryButton>
        </div>
      )}

      {!loading && !err && items.length > 0 && (
        <div className="space-y-2" data-testid="rma-card-list">
          {items.map((rma) => (
            <button
              key={rma.id}
              type="button"
              onClick={() => nav(`/rma-records/${rma.id}`)}
              data-testid={`rma-card-row-${rma.id}`}
              className="w-full flex items-center gap-2 rounded-md border border-slate-200 px-2.5 py-2 text-left hover:border-slate-400 hover:bg-slate-50 transition"
            >
              <span className="font-data text-[11px] text-slate-900 truncate">
                {rma.rma_number}
              </span>
              <RMAStatusBadge status={rma.status} />
              <span className="text-[11px] text-slate-500 ml-auto truncate">
                {RMA_VENDOR_LABEL[rma.vendor] || rma.vendor}
              </span>
            </button>
          ))}
          <SecondaryButton
            type="button"
            onClick={() => setCreateOpen(true)}
            className="w-full mt-1"
            data-testid="rma-card-create-add"
          >
            <PlusIcon size={14} className="mr-1.5" /> Add another RMA
          </SecondaryButton>
        </div>
      )}

      <CreateRMAModal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          load();
        }}
        lockedReportId={reportId}
        lockedReportLabel={lockedLabel}
      />
    </section>
  );
}
